<script>
import { mapState } from 'vuex'
import date from 'locutus/php/datetime/date'
import long2ip from 'locutus/php/network/long2ip'
import PageNavigation from '@components/admin/page-navigation'
import { fetchAssetWithdrawAddressCertificationList } from '@apis/admin/kyc'
import { fetchAssets } from '@apis/admin/asset'
import fromPairs from 'lodash/fromPairs'
import pick from 'lodash/pick'
import map from 'lodash/map'
import mapKeys from 'lodash/mapKeys'

export default {
  components: { PageNavigation },
  data() {
    return {
      loading: false,
      list: [],
      assets: [],
      total_count: 0,
      page: 1,
      total_page: 0,
      page_navigation: {},
      search_params: {},
      excel_headers: [
        ['_no', this.$t('kyc_asset_withdraw_address_certification_list._no')],
        ['seq', this.$t('kyc_asset_withdraw_address_certification_list.seq')],
        [
          'asset_seq',
          this.$t('kyc_asset_withdraw_address_certification_list.asset_seq'),
        ],
        [
          'asset_code',
          this.$t('kyc_asset_withdraw_address_certification_list.asset_code'),
        ],
        [
          'asset_title',
          this.$t('kyc_asset_withdraw_address_certification_list.asset_title'),
        ],
        [
          'bank_code',
          this.$t('kyc_asset_withdraw_address_certification_list.   bank_code'),
        ],
        [
          'withdraw_address',
          this.$t(
            'kyc_asset_withdraw_address_certification_list.withdraw_address'
          ),
        ],
        [
          'holder_name',
          this.$t('kyc_asset_withdraw_address_certification_list.holder_name'),
        ],
        [
          'member_seq',
          this.$t('kyc_asset_withdraw_address_certification_list.member_seq'),
        ],
        [
          'user_id',
          this.$t('kyc_asset_withdraw_address_certification_list.user_id'),
        ],
        [
          'user_name',
          this.$t('kyc_asset_withdraw_address_certification_list.user_name'),
        ],
        [
          'email_address',
          this.$t(
            'kyc_asset_withdraw_address_certification_list.email_address'
          ),
        ],
        [
          'status',
          this.$t('kyc_asset_withdraw_address_certification_list.status'),
        ],
        [
          'is_valid',
          this.$t('kyc_asset_withdraw_address_certification_list.is_valid'),
        ],
        [
          'regdate',
          this.$t('kyc_asset_withdraw_address_certification_list.regdate'),
        ],
        [
          'expdate',
          this.$t('kyc_asset_withdraw_address_certification_list.expdate'),
        ],
        [
          'closed_date',
          this.$t('kyc_asset_withdraw_address_certification_list.closed_date'),
        ],
        [
          'ipaddress',
          this.$t('kyc_asset_withdraw_address_certification_list.ipaddress'),
        ],
      ],
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
  },
  watch: {
    $route(to, from) {
      this.page = this.$route.query.page || 1
      this.loadList().catch((e) => this.$toasted.error(e.message))
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.page = this.$route.query.page || 1
      this.search_params = { ...this.$route.query }
      Promise.all([this.loadList(), this.loadAssets()])
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadList() {
      this.loading = true
      const ret_obj = await fetchAssetWithdrawAddressCertificationList({
        ...this.$route.params,
        ...this.$route.query,
      })

      this.list = ret_obj.data
      this.total_count = ret_obj.total_count
      this.total_page = ret_obj.total_page
      this.page_navigation = ret_obj.page_navigation
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    search() {
      this.$router.push({ query: this.search_params })
    },
    clearAndSearch() {
      this.search_params = pick(this.search_params, 'asset_seq')
      this.search()
    },
    async handleExportExcel() {
      this.loading = true
      try {
        if (
          !(await this.$confirm(
            this.$t(
              'kyc_asset_withdraw_address_certification_list.msg_server_overload'
            ),
            this.$t('admin.modal_confirm'),
            {
              type: 'warning',
            }
          ).catch(() => false))
        )
          return

        const { data } = await fetchAssetWithdrawAddressCertificationList({
          ...this.$route.params,
          ...this.$route.query,
          no_paging: 1,
        })
        const { default: exportJsonToExcel } = await import(
          '@utils/export-json-to-excel'
        )
        exportJsonToExcel({
          header: map(this.excel_headers, ([, title]) => title),
          data: this.format2excel(data),
          filename: this.$t(
            'kyc_asset_withdraw_address_certification_list.KYC_authentication',
            [date('YmdHis')]
          ),
        })
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    format2excel(list) {
      const excel_fields = map(this.excel_headers, ([name]) => name)
      return map(list, (data) => {
        let result = pick(data, excel_fields)
        const keys = Object.keys(result)
        result = mapKeys(
          map(result, (val, key) => {
            switch (key) {
              case 'regdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              case 'expdate':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              case 'closed_date':
                return val ? date('Y-m-d H:i:s', new Date(val)) : ''
              default:
                return val
            }
          }),
          (val, key) => {
            return keys[key]
          }
        )

        return map(excel_fields, (key) => result[key])
      })
    },
    date,
    fromPairs,
    long2ip,
  },
}
</script>

<template>
  <div v-loading="loading">
    <h3 :class="$style.xeAdmin">{{ $t('admin.search') }}</h3>
    <form @submit.prevent="search">
      <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
        <colgroup>
          <col width="20%" />
          <col width="30%" />
          <col width="20%" />
          <col width="30%" />
        </colgroup>
        <tbody>
          <tr>
            <th scope="col">
              <div>
                <select
                  v-model="search_params.search_target"
                  :class="$style.fullWidth"
                >
                  <option
                    v-for="val in [
                      [undefined, $t('admin.search_target')],
                      ['user_id', $t('admin.user_id')],
                      ['email_address', $t('admin.email_address')],
                      ['user_name', $t('admin.user_name')],
                      ['regdate_more', $t('admin.login_log_regdate_more')],
                      ['regdate_less', $t('admin.login_log_regdate_less')],
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </div>
            </th>
            <td>
              <input
                v-model="search_params.search_keyword"
                :placeholder="$t('admin.search_word')"
                type="text"
                :class="[$style.inputTypeText, $style.fullWidth]"
              />
            </td>
            <td colspan="2">
              <ElCheckbox
                v-model="search_params.use_statistics_exclude"
                :false-label="undefined"
                true-label="N"
                >{{
                  $t(
                    'kyc_asset_withdraw_address_certification_list.statistics_exclude_members'
                  )
                }}</ElCheckbox
              >
              <ElCheckbox
                v-model="search_params.member_is_alive"
                :false-label="undefined"
                true-label="Y"
                >{{
                  $t(
                    'kyc_asset_withdraw_address_certification_list.withdrawal_members_excluded'
                  )
                }}</ElCheckbox
              >
            </td>
          </tr>
          <tr>
            <th scope="col"
              ><div>{{ $t('admin.status') }}</div></th
            >
            <td>
              <ElRadioGroup v-model="search_params.status" size="mini">
                <ElRadioButton
                  v-for="val in [
                    [undefined, $t('admin.total')],
                    ...constants.kyc.certification.status,
                  ]"
                  :key="val[0]"
                  :label="val[0]"
                  >{{ val[1] }}</ElRadioButton
                >
              </ElRadioGroup>
            </td>
            <th scope="col"
              ><div>{{ $t('admin.sort') }}</div></th
            >
            <td>
              <select v-model="search_params.sort_order">
                <option
                  v-for="val in [[undefined, $t('admin.regdate')]]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
              <select v-model="search_params.order_type">
                <option
                  v-for="val in [
                    [undefined, $t('admin.desc')],
                    ['asc', $t('admin.asc')],
                  ]"
                  :key="val[1]"
                  :value="val[0]"
                  >{{ val[1] }}</option
                >
              </select>
            </td>
          </tr>
          <tr>
            <th colspan="4" :class="$style.button">
              <a
                href="#"
                :class="[$style.button, $style.red]"
                @click.prevent="clearAndSearch"
                ><span>{{ $t('admin.reset') }}</span></a
              >
              {{ ' ' }}
              <span :class="[$style.button, $style.black]"
                ><input type="submit" :value="$t('admin.submit')"
              /></span>
            </th>
          </tr>
        </tbody>
      </table>
    </form>
    <h3 :class="$style.xeAdmin">{{ $t('admin.kyc_withdraw_list') }}</h3>
    <fieldset :class="$style.clearfix">
      <div :class="$style.fl">
        <a href="#" :class="[$style.button]" @click.prevent="handleExportExcel"
          ><span>{{ $t('admin.excel_download') }}</span></a
        >
      </div>
    </fieldset>
    <ul :class="$style.localNavigation">
      <router-link
        v-for="{ title, seq } in [
          { title: $t('admin.all_show'), seq: undefined },
          ...assets,
        ]"
        :key="seq"
        :class="{ [$style.on]: seq == search_params.asset_seq }"
        :to="{ query: { ...$route.query, asset_seq: seq } }"
        tag="li"
      >
        <a href="#" @click.prevent>{{ title }}</a>
      </router-link>
    </ul>
    <table cellspacing="0" cellpadding="0" :class="$style.colTable">
      <caption v-if="!loading"
        >Total {{ total_count }}, Page {{ page }}/{{ total_page }}</caption
      >
      <thead>
        <tr>
          <th scope="col"
            ><div
              ><a href="#">{{ $t('admin.num') }}</a></div
            ></th
          >
          <template v-if="!$route.params.member_seq">
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_id') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.user_name') }}</div></th
            >
            <th scope="col" :class="$style.quarter_wide"
              ><div>{{ $t('admin.email_address') }}</div></th
            >
          </template>
          <th scope="col"
            ><div>{{ $t('asset') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.bank') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.holder_name') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.withdraw_address') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.status') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.kyc_is_valid') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.close_date') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.kyc_expdate') }}</div></th
          >
          <th scope="col"
            ><div>{{ $t('admin.regdate') }}</div></th
          >
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(data, idx) in list"
          :key="data.seq"
          :class="$style[`bg${idx % 2}`]"
        >
          <td :class="[$style.tCenter, $style.nowrap]">{{ data._no }}</td>
          <template v-if="!$route.params.member_seq">
            <td :class="[$style.tCenter, $style.nowrap]">{{ data.user_id }}</td>
            <td :class="[$style.tCenter, $style.nowrap]">{{
              data.user_name
            }}</td>
            <td :class="[$style.nowrap]">{{ data.email_address }}</td>
          </template>
          <td :class="[$style.wide]">{{ data.asset_title }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            fromPairs(
              constants.kyc.asset_withdraw_address_certification.bank_codes
            )[data.bank_code]
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.holder_name
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.withdraw_address
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            fromPairs(
              constants.kyc.asset_withdraw_address_certification.status
            )[data.status]
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.is_valid === 'Y' ? $t('admin.valid') : $t('admin.invalid')
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.closed_date === null
              ? $t('admin.no_close')
              : date('Y-m-d H:i:s', new Date(data.closed_date))
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            data.expdate === null
              ? $t('admin.no_appoint')
              : date('Y-m-d H:i:s', new Date(data.expdate))
          }}</td>
          <td :class="[$style.tCenter, $style.nowrap]">{{
            date('Y-m-d H:i:s', new Date(data.regdate))
          }}</td>
        </tr>
        <tr v-if="loading">
          <td
            :colspan="$route.params.member_seq ? 10 : 13"
            :class="$style.tCenter"
            >{{ $t('admin.loading') }}</td
          >
        </tr>
        <tr v-else-if="!list.length">
          <td
            :colspan="$route.params.member_seq ? 10 : 13"
            :class="$style.tCenter"
            >{{ $t('admin.no_search_kyc_withdraw') }}</td
          >
        </tr>
      </tbody>
    </table>
    <PageNavigation
      v-if="!loading"
      :total_count="total_count"
      :total_page="page_navigation.total_page"
      :cur_page="page"
      :page_count="page_navigation.page_count"
    ></PageNavigation>
  </div>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
